import { Component, OnInit, OnDestroy, signal, computed, HostListener, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MediatiqueService, AlbumDetail, AlbumMedia } from '../mediatique.service';
import { SwalUtils } from '../../../../shared/utils/swal.utils';
import { PermissionService } from '../../../../core/service/permission/permission.service';

import { isAllowedImage, readImagePreview, IMAGE_FORMAT_ERROR } from '../../../../shared/utils/image-upload';
@Component({
  selector: 'app-mediatique-detail',
  standalone: false,
  templateUrl: './mediatique-detail.component.html',
  styleUrls: ['./mediatique-detail.component.scss'],
})
export class MediatiqueDetailComponent implements OnInit, OnDestroy {
  readonly permService = inject(PermissionService);
  isEditMode = signal(false);
  album = signal<AlbumDetail | null>(null);
  isLoading = signal(true);
  isSaving = signal(false);
  errorMessage = signal<string | null>(null);
  lightboxIndex = signal(-1);

  // Video modal
  activeVideoUrl = signal<SafeResourceUrl | null>(null);

  // Add média modal
  showModal = signal(false);
  modalTab = signal<'image' | 'video'>('image');
  modalVideoLink = signal('');
  modalFiles = signal<{ file: File; preview: string }[]>([]);
  modalError = signal<string | null>(null);

  // Edit fields
  editTitle = signal('');
  editDate = signal('');
  editCategory = signal('');
  editStatus = signal<'ACTI' | 'INAC'>('ACTI');
  editVideoLinks = signal<string[]>([]);

  // Unified media list (saved items)
  localMedia = signal<AlbumMedia[]>([]);

  // Pending new photos (not yet saved)
  pendingFiles = signal<File[]>([]);
  pendingPreviews = signal<string[]>([]);

  // Pending new video links (not yet saved)
  pendingVideoLinks = signal<string[]>([]);

  // Drag state
  dragIndex = signal(-1);
  dragOverIndex = signal(-1);

  // Cleared media ids (to delete on save)
  clearedMediaIds = signal<Set<number>>(new Set());

  // Pending main media id change
  pendingMainMediaId = signal<number | null>(null);

  embedUrl(videoLink: string): SafeResourceUrl | null {
    const url = videoLink.trim();
    const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
    if (yt) return this.sanitizer.bypassSecurityTrustResourceUrl(`https://www.youtube.com/embed/${yt[1]}`);
    const vim = url.match(/vimeo\.com\/(\d+)/);
    if (vim) return this.sanitizer.bypassSecurityTrustResourceUrl(`https://player.vimeo.com/video/${vim[1]}`);
    return null;
  }

  videoThumbnail(videoLink: string): string | null {
    const yt = videoLink.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
    if (yt) return `https://img.youtube.com/vi/${yt[1]}/mqdefault.jpg`;
    return null;
  }

  private destroy$ = new Subject<void>();
  albumId = 0;

  constructor(
    private mediatiqueService: MediatiqueService,
    private route: ActivatedRoute,
    public router: Router,
    private sanitizer: DomSanitizer,
  ) {}

  ngOnInit(): void {
    this.isEditMode.set(this.route.snapshot.url.some(s => s.path === 'edit'));
    this.route.params.pipe(takeUntil(this.destroy$)).subscribe(params => {
      if (params['id']) { this.albumId = +params['id']; this.loadAlbum(); }
    });
  }

  onEdit(): void { this.router.navigate(['/federation/mediatique', this.albumId, 'edit']); }

  async confirmDelete(): Promise<void> {
    const result = await SwalUtils.confirm('Archiver cet album', 'L\'album sera archivé et masqué du site public.', 'Archiver');
    if (!result.isConfirmed) return;
    this.isSaving.set(true);
    this.mediatiqueService.setStatus(this.albumId, 'ARCH').pipe(takeUntil(this.destroy$)).subscribe({
      next: () => this.router.navigate(['/federation/mediatique']),
      error: () => { SwalUtils.error('Erreur', 'Impossible d\'archiver cet album.'); this.isSaving.set(false); }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  openModal(): void {
    this.modalTab.set('image');
    this.modalVideoLink.set('');
    this.modalFiles.set([]);
    this.modalError.set(null);
    this.showModal.set(true);
  }

  closeModal(): void { this.showModal.set(false); }

  onModalFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files || []);
    input.value = '';
    if (!files.length) return;

    const errors: string[] = [];
    for (const file of files) {
      if (!isAllowedImage(file)) { errors.push(`${file.name} : ${IMAGE_FORMAT_ERROR}`); continue; }
      if (file.size > 10 * 1024 * 1024) { errors.push(`${file.name} : fichier trop volumineux (max 10 Mo)`); continue; }
      const entry = { file, preview: '' };
      this.modalFiles.set([...this.modalFiles(), entry]);
      readImagePreview(file, (url) => {
        entry.preview = url;
        this.modalFiles.set([...this.modalFiles()]);
      });
    }
    this.modalError.set(errors.length ? errors.join(' · ') : null);
  }

  removeModalFile(index: number): void {
    const files = [...this.modalFiles()];
    files.splice(index, 1);
    this.modalFiles.set(files);
  }

  confirmAdd(): void {
    if (this.modalTab() === 'image') {
      const files = this.modalFiles();
      if (!files.length) { this.modalError.set('Sélectionnez au moins une image'); return; }
      this.pendingFiles.set([...this.pendingFiles(), ...files.map(f => f.file)]);
      this.pendingPreviews.set([...this.pendingPreviews(), ...files.map(f => f.preview)]);
    } else {
      const link = this.modalVideoLink().trim();
      if (!link) { this.modalError.set('Entrez un lien vidéo'); return; }
      this.pendingVideoLinks.set([...this.pendingVideoLinks(), link]);
    }
    this.closeModal();
  }

  openVideo(videoLink: string): void {
    this.activeVideoUrl.set(this.embedUrl(videoLink));
  }

  closeVideo(): void {
    this.activeVideoUrl.set(null);
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(e: KeyboardEvent): void {
    if (e.key === 'Escape') {
      if (this.activeVideoUrl()) { this.closeVideo(); return; }
      if (this.lightboxIndex() >= 0) this.closeLightbox();
    }
    if (this.lightboxIndex() === -1) return;
    if (e.key === 'ArrowRight') this.nextPhoto();
    if (e.key === 'ArrowLeft') this.prevPhoto();
  }

  private loadAlbum(): void {
    this.isLoading.set(true);
    this.mediatiqueService.getAlbumById(this.albumId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        const data: AlbumDetail = res.data;
        this.album.set(data);
        this.editTitle.set(data.title || '');
        this.editDate.set(this.formatDateForInput(data.event_date));
        this.editCategory.set(data.media_tags || '');
        this.editStatus.set((data.status as 'ACTI' | 'INAC') || 'ACTI');
        this.editVideoLinks.set(data.video_links?.length ? [...data.video_links] : []);
        this.localMedia.set([...(data.media || [])]);
        this.pendingMainMediaId.set(data.media?.find(m => m.is_main)?.id ?? null);
        this.clearedMediaIds.set(new Set());
        this.pendingFiles.set([]);
        this.pendingPreviews.set([]);
        this.pendingVideoLinks.set([]);
        this.isLoading.set(false);
      },
      error: () => { this.errorMessage.set('Erreur lors du chargement de l\'album'); this.isLoading.set(false); }
    });
  }

  // ── Set main media (local only) ────────────────────────────────────────────
  setMainMedia(mediaId: number): void {
    this.pendingMainMediaId.set(mediaId);
    this.localMedia.set(this.localMedia().map(m => ({ ...m, is_main: m.id === mediaId })));
  }

  // ── Clear media item (local only — deleted on save) ────────────────────────
  clearMedia(mediaId: number): void {
    this.clearedMediaIds.set(new Set([...this.clearedMediaIds(), mediaId]));
    this.localMedia.set(this.localMedia().filter(m => m.id !== mediaId));
    if (this.pendingMainMediaId() === mediaId) {
      const remaining = this.localMedia();
      const newMain = remaining.length > 0 ? remaining[0].id : null;
      this.pendingMainMediaId.set(newMain);
      this.localMedia.set(this.localMedia().map((m, i) => ({ ...m, is_main: i === 0 && newMain !== null })));
    }
  }

  // ── Pending photos ─────────────────────────────────────────────────────────
  onAddPhotos(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files || []).filter(f => isAllowedImage(f) && f.size <= 10 * 1024 * 1024);
    if (!files.length) return;
    files.forEach(file => readImagePreview(file, (url) => this.pendingPreviews.set([...this.pendingPreviews(), url])));
    this.pendingFiles.set([...this.pendingFiles(), ...files]);
    input.value = '';
  }

  removePendingVideo(index: number): void {
    const links = [...this.pendingVideoLinks()];
    links.splice(index, 1);
    this.pendingVideoLinks.set(links);
  }

  removePending(index: number): void {
    const files = [...this.pendingFiles()];
    const previews = [...this.pendingPreviews()];
    files.splice(index, 1);
    previews.splice(index, 1);
    this.pendingFiles.set(files);
    this.pendingPreviews.set(previews);
  }

  // ── Drag & drop ────────────────────────────────────────────────────────────
  onDragStart(index: number): void { this.dragIndex.set(index); }

  onDragOver(event: DragEvent, index: number): void {
    event.preventDefault();
    this.dragOverIndex.set(index);
  }

  onDragEnd(): void {
    const from = this.dragIndex();
    const to = this.dragOverIndex();
    this.dragIndex.set(-1);
    this.dragOverIndex.set(-1);
    if (from === -1 || to === -1 || from === to) return;
    const media = [...this.localMedia()];
    const [moved] = media.splice(from, 1);
    media.splice(to, 0, moved);
    this.localMedia.set(media);
  }

  // ── Video link helpers ─────────────────────────────────────────────────────
  addVideoLink(): void { this.editVideoLinks.set([...this.editVideoLinks(), '']); }

  removeVideoLink(index: number): void {
    const links = [...this.editVideoLinks()];
    links.splice(index, 1);
    this.editVideoLinks.set(links);
  }

  updateVideoLink(index: number, value: string): void {
    const links = [...this.editVideoLinks()];
    links[index] = value;
    this.editVideoLinks.set(links);
  }

  // ── Save all changes ───────────────────────────────────────────────────────
  async saveChanges(): Promise<void> {
    if (!this.editTitle().trim()) return;
    const a = this.album();
    if (!a) return;

    const confirmed = await SwalUtils.confirmGreen('Enregistrer', 'Voulez-vous enregistrer toutes les modifications ?', 'Enregistrer');
    if (!confirmed.isConfirmed) return;

    this.isSaving.set(true);

    const steps: (() => Promise<void>)[] = [];

    // 1. Set main media FIRST (before updateAlbum recreates video rows with new IDs)
    const currentMain = a.media?.find(m => m.is_main)?.id ?? null;
    if (this.pendingMainMediaId() !== null && this.pendingMainMediaId() !== currentMain) {
      steps.push(() => this.mediatiqueService.setMainMedia(a.id_media_library, this.pendingMainMediaId()!).toPromise().then(() => {}));
    }

    // 2. Reorder BEFORE updateAlbum recreates video rows
    if (this.localMedia().length) {
      steps.push(() => this.mediatiqueService.reorderMedia(a.id_media_library, this.localMedia().map(m => m.id)).toPromise().then(() => {}));
    }

    // 3. Update title + date + category
    steps.push(() => this.mediatiqueService.updateAlbum(a.id_media_library, {
      title: this.editTitle().trim(),
      event_date: this.editDate(),
      media_tags: this.editCategory() || null,
    }).toPromise().then(() => {}));

    // 4b. Insert pending video links
    if (this.pendingVideoLinks().length) {
      steps.push(() => this.mediatiqueService.addVideoLinks(a.id_media_library, this.pendingVideoLinks()).toPromise().then(() => {}));
    }

    // 4. Status
    if (this.editStatus() !== a.status) {
      steps.push(() => this.mediatiqueService.setStatus(a.id_media_library, this.editStatus()).toPromise().then(() => {}));
    }

    // 5. Upload new photos
    if (this.pendingFiles().length) {
      const form = new FormData();
      this.pendingFiles().forEach(f => form.append('photos', f));
      steps.push(() => this.mediatiqueService.addPhotos(a.id_media_library, form).toPromise().then(() => {}));
    }

    // 6. Delete cleared media items
    for (const mediaId of this.clearedMediaIds()) {
      steps.push(() => this.mediatiqueService.deleteMedia(a.id_media_library, mediaId).toPromise().then(() => {}));
    }

    try {
      for (const step of steps) await step();
      this.isSaving.set(false);
      SwalUtils.success('Enregistré', 'Les modifications ont été enregistrées avec succès.');
      this.router.navigate(['/federation/mediatique', this.albumId]);
    } catch {
      this.isSaving.set(false);
      SwalUtils.error('Erreur', 'Une erreur est survenue lors de l\'enregistrement des modifications.');
    }
  }

  // ── Lightbox (images only) ─────────────────────────────────────────────────
  get imageMedia(): AlbumMedia[] { return this.localMedia().filter(m => m.type === 'image'); }

  openLightbox(index: number): void { this.lightboxIndex.set(index); }
  closeLightbox(): void { this.lightboxIndex.set(-1); }

  nextPhoto(): void {
    const n = this.imageMedia.length;
    if (n) this.lightboxIndex.set((this.lightboxIndex() + 1) % n);
  }

  prevPhoto(): void {
    const n = this.imageMedia.length;
    if (n) this.lightboxIndex.set((this.lightboxIndex() - 1 + n) % n);
  }

  get lightboxPhoto(): string | null {
    const idx = this.lightboxIndex();
    const imgs = this.imageMedia;
    return idx >= 0 && idx < imgs.length ? (imgs[idx].file_link ?? null) : null;
  }

  get coverPhoto(): string | null {
    const main = this.localMedia().find(m => m.is_main);
    if (main) {
      if (main.type === 'image') return main.file_link;
      if (main.type === 'video' && main.video_link) return this.videoThumbnail(main.video_link);
    }
    const firstImg = this.localMedia().find(m => m.type === 'image');
    return firstImg?.file_link ?? null;
  }

  onBack(): void { this.router.navigate(['/federation/mediatique']); }

  formatDate(dateStr: string): string {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
  }

  private formatDateForInput(dateStr: string): string {
    if (!dateStr) return '';
    try { return new Date(dateStr).toISOString().split('T')[0]; } catch { return dateStr; }
  }
}
